/**
 * WifiControllerForm — Formulario full-screen para controladores de red.
 * Pestañas: General / APs vinculados / Notas
 * SmartButtons: Vendor, Tipo, APs, Estado
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  WifiIcon,
  ServerStackIcon,
  CpuChipIcon,
  SparklesIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import DocSmartButton from './DocSmartButton';
import CiActivityPanel from './common/CiActivityPanel';
import DeviceAccessPanel from './DeviceAccessPanel';
import FormCardsShell from './FormCardsShell';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import useNavGuard from '../hooks/useNavGuard';
import '../styles/DetailView.css';
import TabButton from './TabButton';
import ConnectionDiagram from './ConnectionDiagram';
import DetailCrumb from './DetailCrumb';

// ── Constantes ────────────────────────────────────────────────────────────────

const VENDORS = [
  { value: 'ubiquiti',  label: 'Ubiquiti UniFi' },
  { value: 'cisco',     label: 'Cisco (WLC / Catalyst)' },
  { value: 'aruba',     label: 'HPE Aruba' },
  { value: 'ruckus',    label: 'Ruckus / CommScope' },
  { value: 'meraki',    label: 'Cisco Meraki' },
  { value: 'juniper',   label: 'Juniper Mist' },
  { value: 'cambium',   label: 'Cambium Networks' },
  { value: 'extreme',   label: 'Extreme Networks' },
  { value: 'tp_link',   label: 'TP-Link / Omada' },
  { value: 'zyxel',     label: 'Zyxel' },
  { value: 'mikrotik',  label: 'MikroTik' },
  { value: 'fortinet',  label: 'Fortinet FortiAP' },
  { value: 'other',     label: 'Otro' },
];

const CTRL_TYPES = [
  { value: 'hardware_appliance', label: 'Appliance físico' },
  { value: 'virtual_appliance',  label: 'Appliance virtual (VM / Docker)' },
  { value: 'cloud',              label: 'Cloud-managed' },
  { value: 'embedded',           label: 'Embebido en AP / switch' },
  { value: 'none',               label: 'Sin controlador (standalone)' },
];

const STATUS_COLORS = {
  active:      { bg: 'rgba(16,185,129,.15)',   color: '#6ee7b7', label: 'Activo' },
  maintenance: { bg: 'rgba(234,179,8,.15)',    color: '#fde68a', label: 'Mantenimiento' },
  decom:       { bg: 'rgba(239,68,68,.15)',    color: '#fca5a5', label: 'Decom' },
  planned:     { bg: 'rgba(59,130,246,.15)',   color: '#93c5fd', label: 'Planificado' },
};

const EMPTY_FORM = {
  name: '', hostname: '', vendor: 'ubiquiti', model: '',
  serial_number: '', asset_tag: '', firmware_version: '',
  controller_type: 'hardware_appliance',
  rack_element_id: '',
  max_aps: '', max_clients: '', management_ip: '',
  status: 'active', notes: '',
};

// ── SSID constants ─────────────────────────────────────────────────────────────────────
const SECURITY_OPTIONS = [
  { value: 'open',            label: 'Abierta (sin contraseña)',   isEnterprise: false },
  { value: 'wpa2_psk',        label: 'WPA2-Personal (PSK)',        isEnterprise: false },
  { value: 'wpa2_enterprise', label: 'WPA2-Enterprise (RADIUS)',   isEnterprise: true  },
  { value: 'wpa3_psk',        label: 'WPA3-Personal (SAE)',        isEnterprise: false },
  { value: 'wpa3_enterprise', label: 'WPA3-Enterprise (RADIUS)',   isEnterprise: true  },
  { value: 'wpa3_transition', label: 'WPA2/WPA3 Transition Mode',  isEnterprise: false },
  { value: 'owe',             label: 'OWE (Enhanced Open)',        isEnterprise: false },
];

const BAND_FILTER_OPTIONS = [
  { value: 'all',      label: 'Todas las bandas' },
  { value: '2.4_only', label: 'Solo 2.4 GHz' },
  { value: '5_only',   label: 'Solo 5 GHz' },
  { value: '6_only',   label: 'Solo 6 GHz' },
  { value: 'no_24',    label: 'Sin 2.4 GHz (5 + 6 GHz)' },
];

// Colores de los chips de seguridad de los SSID: tono medio saturado + fondo
// translúcido, legible tanto en tema oscuro como claro (guía del design system).
const SECURITY_COLORS = {
  open:            { bg: 'rgba(220,38,38,.16)',  color: '#dc2626' },
  wpa2_psk:        { bg: 'rgba(217,119,6,.16)',  color: '#d97706' },
  wpa2_enterprise: { bg: 'rgba(37,99,235,.16)',  color: '#2563eb' },
  wpa3_psk:        { bg: 'rgba(22,163,74,.16)',  color: '#16a34a' },
  wpa3_enterprise: { bg: 'rgba(124,58,237,.16)', color: '#7c3aed' },
  wpa3_transition: { bg: 'rgba(37,99,235,.13)',  color: '#2563eb' },
  owe:             { bg: 'rgba(5,150,105,.14)',  color: '#059669' },
};

const EMPTY_SSID = {
  name: '', security_mode: 'wpa2_psk',
  radius_server_id: null, radius_server: '', radius_port: 1812,
  vlan_id: '', band_filter: 'all',
  hidden: false, client_isolation: false,
  status: 'active', notes: '',
};

// ── Componente ────────────────────────────────────────────────────────────────

export default function WifiControllerForm({ controller, companyId, onClose, onSave, initialMode, variant = 'classic', navItems = [], navIndex = -1, onNavigate }) {
  const isCards = variant === 'cards';
  const isEditing = Boolean(controller?.id);

  /* Modo de la ficha: 'view' entra en LECTURA (lo que abre el clic en la fila del
     listado), 'edit'/'create' editan. Sin `initialMode`, edita como siempre. */
  const [editing, setEditing] = useState(initialMode ? initialMode !== 'view' : true);
  const [form,    setForm]    = useState(EMPTY_FORM);
  const [linkedAPs, setLinkedAPs] = useState([]);
  const [supportElements, setSupportElements] = useState([]);
  const [tab,     setTab]     = useState(isCards ? 'resumen' : 'general');
  const [accessCount, setAccessCount] = useState(0);
  const [saving,     setSaving]     = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [isDirty,    setIsDirty]    = useState(false);
  const [apiError, setApiError] = useState('');
  const [ssids,       setSsids]       = useState([]);
  const [editSsid,    setEditSsid]    = useState(null);
  const [ssidSaving,  setSsidSaving]  = useState(false);
  const [ssidError,   setSsidError]   = useState('');
  const [radiusServers, setRadiusServers] = useState([]);

  // Cambios sin guardar al pasar de ficha o cerrar.
  const navGuard = useNavGuard({
    dirty: isDirty,
    saving,
    entity: 'este controlador',
    actionLabel: 'pasas a otro controlador o cierras la ficha',
    save: () => handleSave({ keepEditing: editing }),
  });

  // Bootstrap
  useEffect(() => {
    if (controller) {
      setForm({
        name:            controller.name || '',
        hostname:        controller.hostname || '',
        vendor:          controller.vendor || 'ubiquiti',
        model:           controller.model || '',
        serial_number:   controller.serial_number || '',
        asset_tag:       controller.asset_tag || '',
        firmware_version: controller.firmware_version || '',
        controller_type: controller.controller_type || 'hardware_appliance',
        rack_element_id: controller.rack_element_id ? String(controller.rack_element_id) : '',
        max_aps:         controller.max_aps ?? '',
        max_clients:     controller.max_clients ?? '',
        management_ip:   controller.management_ip || '',
        status:          controller.status || 'active',
        notes:           controller.notes || '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setIsDirty(false);
  }, [controller]);

  // Inicializar SSIDs desde el controlador (ya vienen en el to_dict)
  useEffect(() => {
    setSsids(controller?.ssids || []);
    setEditSsid(null);
  }, [controller]);

  // Cargar APs vinculados al editar
  const loadLinkedAPs = useCallback(async () => {
    if (!isEditing || !companyId) return;
    try {
      const res = await api.getWifiAPs(companyId, { controller_id: controller.id });
      setLinkedAPs(res.data || []);
    } catch (e) {
      console.error('Load linked APs error', e);
    }
  }, [isEditing, companyId, controller]);

  useEffect(() => { loadLinkedAPs(); }, [loadLinkedAPs]);

  // Cargar servidores RADIUS
  useEffect(() => {
    if (!companyId) return;
    api.getWifiRadiusServers(companyId).then(r => setRadiusServers(r.data || [])).catch(() => {});
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    api.getElements(companyId, { element_type: 'tray' })
      .then(r => setSupportElements(r.data || []))
      .catch(() => setSupportElements([]));
  }, [companyId]);

  useEffect(() => {
    if (form.controller_type !== 'hardware_appliance' && form.rack_element_id) {
      setForm(f => ({ ...f, rack_element_id: '' }));
      setIsDirty(true);
    }
  }, [form.controller_type, form.rack_element_id]);

  const sf = (k, v) => { setForm(f => ({ ...f, [k]: v })); setIsDirty(true); };
  const handleSuggest = async () => {
    try {
      setSuggesting(true);
      const res = await api.suggestName({ type: 'wifi_controller', companyId, consume: true });
      const suggested = res.data?.suggested_name || res.data?.name || '';
      if (suggested) sf('name', suggested);
    } catch {} finally { setSuggesting(false); }
  };
  const handleChange = e => sf(e.target.name, e.target.value);

  // Guardar
  const handleSave = async ({ keepEditing = false } = {}) => {
    if (!form.name.trim()) { setApiError('El nombre es obligatorio'); return false; }
    setSaving(true); setApiError('');
    try {
      const payload = {
        ...form,
        rack_element_id: form.rack_element_id !== '' ? Number(form.rack_element_id) : null,
        max_aps:     form.max_aps     !== '' ? Number(form.max_aps) : null,
        max_clients: form.max_clients !== '' ? Number(form.max_clients) : null,
      };
      let creado = null;
      if (isEditing) await api.updateWifiController(controller.id, payload, companyId);
      else           creado = registroGuardado(await api.createWifiController(payload, companyId));
      // Alta: el listado adopta el registro creado y la ficha sigue abierta ya
      // con id, así se encienden las pestañas que necesitan estar guardado.
      onSave?.(creado);
      setIsDirty(false);
      if (isCards && !keepEditing) setEditing(false);
      return true;
    } catch (e) { setApiError(e.message); return false; }
    finally { setSaving(false); }
  };

  const handleDiscard = () => {
    if (controller) {
      setForm({
        name:            controller.name || '',
        hostname:        controller.hostname || '',
        vendor:          controller.vendor || 'ubiquiti',
        model:           controller.model || '',
        serial_number:   controller.serial_number || '',
        asset_tag:       controller.asset_tag || '',
        firmware_version: controller.firmware_version || '',
        controller_type: controller.controller_type || 'hardware_appliance',
        rack_element_id: controller.rack_element_id ? String(controller.rack_element_id) : '',
        max_aps:         controller.max_aps ?? '',
        max_clients:     controller.max_clients ?? '',
        management_ip:   controller.management_ip || '',
        status:          controller.status || 'active',
        notes:           controller.notes || '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setIsDirty(false);
    setApiError('');
  };

  // ── SSID handlers ──────────────────────────────────────────────────────────────────

  const handleSsidSave = async (ssidData) => {
    if (!ssidData.name.trim()) { setSsidError('El nombre del SSID es obligatorio'); return; }
    setSsidSaving(true); setSsidError('');
    try {
      const payload = {
        ...ssidData,
        controller_id: controller.id,
        vlan_id: ssidData.vlan_id !== '' ? Number(ssidData.vlan_id) : null,
        radius_port: Number(ssidData.radius_port) || 1812,
      };
      if (ssidData.id) {
        const res = await api.updateWifiSSID(ssidData.id, payload, companyId);
        setSsids(prev => prev.map(s => s.id === ssidData.id ? res.data : s));
      } else {
        const res = await api.createWifiSSID(payload, companyId);
        setSsids(prev => [...prev, res.data]);
      }
      setEditSsid(null);
    } catch (e) { setSsidError(e.message); }
    finally { setSsidSaving(false); }
  };

  const handleSsidDelete = async (ssidId) => {
    if (!window.confirm('¿Eliminar este SSID?')) return;
    try {
      await api.deleteWifiSSID(ssidId, companyId);
      setSsids(prev => prev.filter(s => s.id !== ssidId));
      if (editSsid?.id === ssidId) setEditSsid(null);
    } catch (e) { setApiError(e.message); }
  };

  // Datos derivados
  const statusInfo  = STATUS_COLORS[form.status]  || STATUS_COLORS.active;
  const ctrlType    = CTRL_TYPES.find(c => c.value === form.controller_type)?.label || '—';
  const vendorLabel = VENDORS.find(v => v.value === form.vendor)?.label || form.vendor;
  const apCount     = isEditing ? linkedAPs.length : 0;
  const ssidCount   = ssids.length;
  const supportElement = supportElements.find(el => String(el.id) === String(form.rack_element_id));

  const STATUS_CYCLE = ['active', 'maintenance', 'planned', 'decom'];

  // ── Datos de la variante tarjetas ────────────────────────────────────────
  const ESTADO_COLOR = { active: '#16a34a', maintenance: '#f59e0b', planned: '#0ea5e9', decom: '#dc2626' };
  const estadoColor = ESTADO_COLOR[form.status] || '#94a3b8';
  /* En tarjetas el panel se marca `sf2-pane` para que sus secciones sean tarjetas;
     en la clásica se queda en `dv-tab-pane` (no cambia nada). */
  const paneClass = isCards ? 'dv-tab-pane sf2-pane' : 'dv-tab-pane';
  /** Cancelar edición: recarga lo guardado y sale de edición **sin cerrar** la ficha. */
  const handleCancelEdit = () => { handleDiscard(); setEditing(false); };

  /* Pasar de ficha a ficha sin salir al listado (§12.A5). */
  const irAControlador = (destino) => {
    if (!destino || saving) return;
    navGuard.request(() => {
      setIsDirty(false);
      onNavigate?.(destino);
    });
  };

  // En tarjetas no hay pestaña «General»: identificación, capacidad y anclaje van al Resumen.
  const tabsDef = isCards
    ? [
        { key: 'resumen', label: 'General' },
        { key: 'ssids',   label: `SSIDs (${ssidCount})` },
        { key: 'aps',     label: `APs vinculados (${apCount})` },
        ...(isEditing ? [{ key: 'access', label: `Accesos${accessCount > 0 ? ` (${accessCount})` : ''}` }] : []),
      ]
    : [
        { key: 'general', label: 'General' },
        { key: 'ssids',   label: `SSIDs${ssidCount > 0 ? ` (${ssidCount})` : ''}` },
        ...(isEditing ? [{ key: 'access', label: `Accesos${accessCount > 0 ? ` (${accessCount})` : ''}` }] : []),
        { key: 'aps',     label: `APs vinculados${isEditing ? ` (${apCount})` : ''}` },
      ];

  return (
    <div className="dv-overlay">

      {/* ── TopBar ── */}
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={() => navGuard.request(() => onClose())}>
          <ArrowLeftIcon style={{ width: 15, height: 15 }} />
          Controladores de red
        </button>
        <DetailCrumb label={form.name} />

        {/* ── Navegación entre fichas: a la izquierda del spacer ── */}
        <FormPager
          items={navItems}
          index={navIndex}
          label="Controlador"
          disabled={saving}
          onChange={irAControlador}
        />
        {navGuard.dialog}

        <div className="dv-topbar-spacer" />
        {apiError && (
          <span style={{ color: '#dc2626', fontSize: '0.78rem', maxWidth: 300 }}>
            ⚠ {apiError}
          </span>
        )}
        <div className="dv-topbar-actions">
          {isCards ? (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
              <XMarkIcon style={{ width: 14, height: 14 }} /> Cerrar
            </button>
          ) : (
          <>
          <span
            className="dv-status-badge"
            style={{ background: statusInfo.bg, color: statusInfo.color, cursor: editing ? 'pointer' : 'default' }}
            title={editing ? 'Clic para cambiar estado' : statusInfo.label}
            onClick={() => {
              if (!editing) return;
              const idx = STATUS_CYCLE.indexOf(form.status);
              sf('status', STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length]);
            }}
          >
            ● {statusInfo.label}
          </span>
          {!editing ? (
            <>
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon style={{ width: 14, height: 14 }} /> Cerrar
              </button>
              <button className="dv-btn dv-btn-primary" type="button" onClick={() => setEditing(true)}>
                <CheckIcon style={{ width: 14, height: 14 }} /> Editar
              </button>
            </>
          ) : (
            <>
              {isDirty && (
                <button className="dv-btn dv-btn-secondary" type="button" onClick={handleDiscard} disabled={saving}>
                  <XMarkIcon style={{ width: 14, height: 14 }} /> Descartar
                </button>
              )}
              <button
                className="dv-btn dv-btn-primary"
                type="button"
                onClick={handleSave}
                disabled={saving || !isDirty}
              >
                {saving ? <span className="spinner-border spinner-border-sm" /> : <CheckIcon style={{ width: 14, height: 14 }} />}
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </>
          )}
          </>
          )}
        </div>
      </div>

      {/* ── SmartBar (solo en la ficha clásica) ── */}
      {!isCards && (
      <div className="dv-smartbar">
        <div className="dv-smart-btn" title={vendorLabel}>
          <span className="dv-smart-btn-icon">
            <ServerStackIcon style={{ width: 18, height: 18, color: '#2563eb' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {vendorLabel}
          </span>
          <span className="dv-smart-btn-label">Vendor</span>
        </div>

        <div className="dv-smart-sep" />

        <div className="dv-smart-btn" title={ctrlType}>
          <span className="dv-smart-btn-icon">
            <CpuChipIcon style={{ width: 18, height: 18, color: '#7c3aed' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {ctrlType}
          </span>
          <span className="dv-smart-btn-label">Tipo</span>
        </div>

        <div className="dv-smart-sep" />

        <button
          type="button"
          className="dv-smart-btn"
          onClick={() => setTab('aps')}
          title="Ver APs vinculados"
        >
          <span className="dv-smart-btn-icon">
            <WifiIcon style={{ width: 18, height: 18, color: apCount > 0 ? '#0369a1' : '#94a3b8' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ color: apCount > 0 ? '#0369a1' : '#94a3b8' }}>
            {apCount}
          </span>
          <span className="dv-smart-btn-label">APs</span>
        </button>

        <div className="dv-smart-sep" />

        <button
          type="button"
          className="dv-smart-btn"
          onClick={() => { setTab('ssids'); setEditSsid(null); }}
          title="Ver SSIDs"
        >
          <span className="dv-smart-btn-icon">
            <span style={{ fontSize: 16 }}>📡</span>
          </span>
          <span className="dv-smart-btn-value" style={{ color: ssidCount > 0 ? '#7c3aed' : '#94a3b8' }}>
            {ssidCount}
          </span>
          <span className="dv-smart-btn-label">SSIDs</span>
        </button>

        <div className="dv-smart-sep" />

        <div className="dv-smart-btn" title={supportElement ? `${supportElement.name}${supportElement.rack_name ? ` · ${supportElement.rack_name}` : ''}` : 'Sin bandeja soporte'}>
          <span className="dv-smart-btn-icon">
            <span style={{ fontSize: 16 }}>🧰</span>
          </span>
          <span className="dv-smart-btn-value" style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {supportElement ? supportElement.name : '—'}
          </span>
          <span className="dv-smart-btn-label">Bandeja</span>
        </div>

        <div className="dv-smart-sep" />

        <div className="dv-smart-btn">
          <span className="dv-smart-btn-icon">
            <span style={{ fontSize: 16 }}>{form.management_ip ? '🌐' : '—'}</span>
          </span>
          <span className="dv-smart-btn-value" style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
            {form.management_ip || '—'}
          </span>
          <span className="dv-smart-btn-label">IP gestión</span>
        </div>

        {isEditing && (
          <>
            <div className="dv-smart-sep" />
            <DocSmartButton elementType="wifi_controller" elementId={controller.id} elementName={form.name} companyId={companyId} />
          </>
        )}
      </div>
      )}

      {/* ── Body ── (en lectura se apaga de golpe con el fieldset) ── */}
      <div className="dv-body">
        <fieldset className={`dv-fieldset${isCards ? ' sf2-main' : ''}`} disabled={isCards ? false : !editing}>
        <form className={`dv-main${isCards ? ' sf2-main' : ''}`} onSubmit={e => e.preventDefault()}>

          <FormCardsShell
            isCards={isCards}
            header={isCards ? (
              <>
                <div className="sf2-head">
                  <div className="sf2-head-icon" aria-hidden="true">
                    <WifiIcon style={{ width: 20, height: 20 }} />
                  </div>
                  <div className="sf2-head-title">
                    <div className="sf2-title-row">
                      <input
                        className="sf2-head-input"
                        value={form.name}
                        onChange={e => sf('name', e.target.value)}
                        placeholder="Nombre del controlador…"
                        disabled={!editing}
                      />
                      {editing ? (
                        <>
                          <button
                            type="button"
                            className="sf2-title-btn sf2-title-btn--ok"
                            onClick={handleSave}
                            disabled={saving}
                            title="Guardar cambios"
                          >
                            {saving ? <span className="dv-spinner" /> : <CheckIcon />}
                          </button>
                          <button
                            type="button"
                            className="sf2-title-btn"
                            onClick={handleCancelEdit}
                            disabled={saving}
                            title="Cancelar la edición y descartar cambios"
                          >
                            <XMarkIcon />
                          </button>
                        </>
                      ) : (
                        <button type="button" className="sf2-title-btn" onClick={() => setEditing(true)} title="Editar la ficha">
                          <PencilSquareIcon />
                        </button>
                      )}
                    </div>
                    <div className="sf2-crumb">
                      {[ctrlType, form.hostname || form.management_ip].filter(Boolean).join(' › ')}
                    </div>
                  </div>
                  <div className="sf2-badges">
                    <span className="sf2-badge"><i style={{ '--c': estadoColor }} />{statusInfo.label}</span>
                    <span className="sf2-badge">{vendorLabel}</span>
                    {form.firmware_version && <span className="sf2-badge">FW {form.firmware_version}</span>}
                  </div>
                </div>
                <div className="sf2-chips">
                  <span className="sf2-chip sf2-chip--flat"><b>{apCount}</b><span>APs</span></span>
                  <span className="sf2-chip sf2-chip--flat"><b>{ssidCount}</b><span>SSIDs</span></span>
                  <span className="sf2-chip sf2-chip--flat"><b>{form.management_ip || '—'}</b><span>IP gestión</span></span>
                  {supportElement && (
                    <span className="sf2-chip sf2-chip--flat"><b>{supportElement.name}</b><span>bandeja</span></span>
                  )}
                  {accessCount > 0 && (
                    <button type="button" className="sf2-chip" onClick={() => setTab('access')}>
                      <b>{accessCount}</b><span>accesos</span>
                    </button>
                  )}
                  {controller?.id && (
                    <DocSmartButton elementType="wifi_controller" elementId={controller.id} elementName={form.name} companyId={companyId} variant="chip" />
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Título */}
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <input
                    className="dv-record-title"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Nombre del controlador…"
                    style={{ flex: 1 }}
                  />
                  <button type="button" title="Sugerir nombre" onClick={handleSuggest} disabled={suggesting}
                    style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: 8, cursor: suggesting ? 'wait' : 'pointer', color: suggesting ? '#94a3b8' : '#7c3aed', flexShrink: 0 }}>
                    <SparklesIcon style={{ width: 18, height: 18 }} />
                  </button>
                </div>
              </>
            )}
            tabs={(
              <div className="dv-tabs-nav">
            {tabsDef.map(t => (
              <TabButton
                key={t.key}
                type="button"
                className={`dv-tab-btn${tab === t.key ? ' active' : ''}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </TabButton>
            ))}
              </div>
            )}
          >
          <fieldset className="dv-fieldset" disabled={!editing}>

          {/* ── Tab: Resumen (solo tarjetas) ── */}
          {isCards && tab === 'resumen' && (
            <div className="dv-tab-pane">
              <div className="sf2-hl" style={{ marginBottom: 12 }}>
                <div className="sf2-kpi"><span>APs vinculados</span><b>{apCount}</b><small>de {form.max_aps || '—'} licenciados</small></div>
                <div className="sf2-kpi"><span>SSIDs</span><b>{ssidCount}</b></div>
                <div className="sf2-kpi"><span>Máx. clientes</span><b>{form.max_clients || '—'}</b></div>
                <div className="sf2-kpi"><span>Accesos</span><b>{accessCount}</b></div>
                <div className="sf2-kpi"><span>Estado</span><b>{statusInfo.label}</b></div>
              </div>

              <div className="sf2-grid sf2-grid--2">
                <div className="sf2-card">
                  <h3>Identificación{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field"><label>Nombre</label><div className="v">{form.name || '—'}</div></div>
                    <div className="sf2-field">
                      <label>Hostname / FQDN</label>
                      {editing
                        ? <input value={form.hostname} onChange={e => sf('hostname', e.target.value)} placeholder="wlc.empresa.local" />
                        : <div className="v">{form.hostname || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>IP de gestión</label>
                      {editing
                        ? <input value={form.management_ip} onChange={e => sf('management_ip', e.target.value)} placeholder="10.0.0.10" />
                        : <div className="v">{form.management_ip || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Fabricante</label>
                      {editing ? (
                        <select value={form.vendor} onChange={e => sf('vendor', e.target.value)}>
                          {VENDORS.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                        </select>
                      ) : <div className="v">{vendorLabel || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Modelo</label>
                      {editing
                        ? <input value={form.model} onChange={e => sf('model', e.target.value)} placeholder="Cisco 3504, UDM Pro…" />
                        : <div className="v">{form.model || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Tipo de controlador</label>
                      {editing ? (
                        <select value={form.controller_type} onChange={e => sf('controller_type', e.target.value)}>
                          {CTRL_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      ) : <div className="v">{ctrlType}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Estado</label>
                      {editing ? (
                        <select value={form.status} onChange={e => sf('status', e.target.value)}>
                          <option value="active">Activo</option>
                          <option value="maintenance">Mantenimiento</option>
                          <option value="planned">Planificado</option>
                          <option value="decom">Decom</option>
                        </select>
                      ) : <div className="v">{statusInfo.label}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Firmware</label>
                      {editing
                        ? <input value={form.firmware_version} onChange={e => sf('firmware_version', e.target.value)} placeholder="8.5.6.12873" />
                        : <div className="v">{form.firmware_version || '—'}</div>}
                    </div>
                  </div>
                </div>

                <div className="sf2-card">
                  <h3>Capacidad y anclaje{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field">
                      <label>Máx. APs licenciados</label>
                      {editing
                        ? <input type="number" min="0" value={form.max_aps} onChange={e => sf('max_aps', e.target.value)} placeholder="64" />
                        : <div className="v">{form.max_aps || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Máx. clientes</label>
                      {editing
                        ? <input type="number" min="0" value={form.max_clients} onChange={e => sf('max_clients', e.target.value)} placeholder="2048" />
                        : <div className="v">{form.max_clients || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Número de serie</label>
                      {editing
                        ? <input value={form.serial_number} onChange={e => sf('serial_number', e.target.value)} />
                        : <div className="v">{form.serial_number || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Asset tag</label>
                      {editing
                        ? <input value={form.asset_tag} onChange={e => sf('asset_tag', e.target.value)} />
                        : <div className="v">{form.asset_tag || '—'}</div>}
                    </div>
                    {form.controller_type === 'hardware_appliance' && (
                      <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                        <label>Bandeja soporte</label>
                        {editing ? (
                          <select value={form.rack_element_id} onChange={e => sf('rack_element_id', e.target.value)}>
                            <option value="">— sin anclar a bandeja —</option>
                            {supportElements.map(el => (
                              <option key={el.id} value={el.id}>
                                {el.name}{el.rack_name ? ` · ${el.rack_name}` : ''}{el.position_u ? ` · U${el.position_u}` : ''}
                              </option>
                            ))}
                          </select>
                        ) : <div className="v">{supportElement ? supportElement.name : '—'}</div>}
                      </div>
                    )}
                  </div>
                  <span className="sf2-hint">
                    La bandeja sirve para equipos físicos que descansan sobre ella; varios pueden compartirla sin competir por una U.
                  </span>
                </div>

                <div className="sf2-card">
                  <h3>Relaciones</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field"><label>APs vinculados</label><div className="v">{apCount}</div></div>
                    <div className="sf2-field"><label>SSIDs definidos</label><div className="v">{ssidCount}</div></div>
                    <div className="sf2-field"><label>Accesos registrados</label><div className="v">{accessCount}</div></div>
                    <div className="sf2-field"><label>Bandeja</label><div className="v">{supportElement ? supportElement.name : '—'}</div></div>
                  </div>
                  <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    <span
                      className="sf2-link"
                      role="button"
                      tabIndex={0}
                      onClick={() => setTab('ssids')}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTab('ssids'); } }}
                    >Ver SSIDs →</span>
                    <span
                      className="sf2-link"
                      role="button"
                      tabIndex={0}
                      onClick={() => setTab('aps')}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTab('aps'); } }}
                    >Ver APs vinculados →</span>
                  </div>
                </div>

                <div className="sf2-card">
                  <h3>Notas{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 1 }}>
                    <div className="sf2-field">
                      <label>Notas</label>
                      {editing ? (
                        <textarea
                          className="dv-input"
                          rows={4}
                          value={form.notes}
                          onChange={e => sf('notes', e.target.value)}
                          placeholder="Observaciones del controlador…"
                        />
                      ) : <div className="v">{form.notes || '—'}</div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: General ── */}
          {tab === 'general' && (
            <div className={paneClass}>

              <div className="dv-section">
                <div className="dv-section-title">Identificación</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Nombre <span className="text-danger">*</span></label>
                    <input className="dv-input" name="name" value={form.name} onChange={handleChange} placeholder="WLC-CENTRAL-01" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Hostname / FQDN</label>
                    <input className="dv-input" name="hostname" value={form.hostname} onChange={handleChange} placeholder="wlc.empresa.local" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">IP de gestión</label>
                    <input className="dv-input" name="management_ip" value={form.management_ip} onChange={handleChange} placeholder="10.0.0.10" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Fabricante</label>
                    <select className="dv-select" name="vendor" value={form.vendor} onChange={handleChange}>
                      {VENDORS.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Modelo</label>
                    <input className="dv-input" name="model" value={form.model} onChange={handleChange} placeholder="Cisco 3504, UniFi Dream Machine Pro…" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Tipo de controlador</label>
                    <select className="dv-select" name="controller_type" value={form.controller_type} onChange={handleChange}>
                      {CTRL_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <div className="dv-section-title">Capacidad</div>
                <div className="row g-3">
                  <div className="col-md-3">
                    <label className="form-label">Máx. APs licenciados</label>
                    <input className="dv-input" name="max_aps" type="number" min="0" value={form.max_aps} onChange={handleChange} placeholder="64" />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Máx. clientes</label>
                    <input className="dv-input" name="max_clients" type="number" min="0" value={form.max_clients} onChange={handleChange} placeholder="2048" />
                  </div>
                </div>
              </div>

              {form.controller_type === 'hardware_appliance' && (
                <div className="dv-section">
                  <div className="dv-section-title">Anclaje físico</div>
                  <div className="row g-3">
                    <div className="col-md-8">
                      <label className="form-label">Bandeja soporte</label>
                      <select className="dv-select" name="rack_element_id" value={form.rack_element_id} onChange={handleChange}>
                        <option value="">— sin anclar a bandeja —</option>
                        {supportElements.map(el => (
                          <option key={el.id} value={el.id}>
                            {el.name}{el.rack_name ? ` · ${el.rack_name}` : ''}{el.position_u ? ` · U${el.position_u}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12">
                      <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                        Úsalo para dispositivos físicos que descansan sobre una bandeja. Varios equipos pueden compartir la misma bandeja sin competir por una U propia.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="dv-section">
                <div className="dv-section-title">Inventario</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Número de serie</label>
                    <input className="dv-input" name="serial_number" value={form.serial_number} onChange={handleChange} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Asset tag</label>
                    <input className="dv-input" name="asset_tag" value={form.asset_tag} onChange={handleChange} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Versión firmware</label>
                    <input className="dv-input" name="firmware_version" value={form.firmware_version} onChange={handleChange} placeholder="8.5.6.12873" />
                  </div>
                </div>
              </div>

            </div>
          )}
          {/* ── Tab: Accesos ── */}
          {tab === 'access' && isEditing && (
            <div className={paneClass}>
              <DeviceAccessPanel
                objectType="wifi_controller"
                objectId={controller?.id}
                companyId={companyId}
                elementName={controller?.name || form.name}
                defaultHost={form.management_ip}
                onCountChange={setAccessCount}
                canEdit={editing}
              />
            </div>
          )}

          {/* ── Tab: SSIDs ── */}
          {tab === 'ssids' && (
            <div className={paneClass}>
              {!isEditing ? (
                <p style={{ color: '#94a3b8', padding: '1rem 0' }}>Guarda el controlador primero para poder añadir SSIDs.</p>
              ) : (
                <>
                  {/* Tabla de SSIDs */}
                  {ssids.length > 0 && (
                    <div className="wol-table-wrap" style={{ marginBottom: '1rem' }}>
                      <table className="wol-table">
                        <thead>
                          <tr>
                            <th>Nombre (SSID)</th>
                            <th>Seguridad</th>
                            <th>Banda</th>
                            <th>VLAN</th>
                            <th>Oculto</th>
                            <th>Estado</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {ssids.map(s => {
                            const sc = SECURITY_COLORS[s.security_mode] || {};
                            const isActive = s.status === 'active';
                            return (
                              <tr key={s.id} style={{ background: editSsid?.id === s.id ? 'rgba(var(--accent-rgb), .10)' : undefined }}>
                                <td><strong style={{ fontFamily: 'monospace' }}>{s.name}</strong></td>
                                <td>
                                  <span style={{ fontSize: '0.75rem', fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: sc.bg || '#f3f4f6', color: sc.color || '#374151' }}>
                                    {s.security_mode_label || s.security_mode}
                                  </span>
                                  {(s.security_mode === 'wpa2_enterprise' || s.security_mode === 'wpa3_enterprise') && s.radius_server && (
                                    <span style={{ fontSize: '0.72rem', color: 'var(--accent)', marginLeft: 6 }}>RADIUS: {s.radius_server}</span>
                                  )}
                                </td>
                                <td style={{ fontSize: '0.82rem' }}>{s.band_filter_label || s.band_filter}</td>
                                <td style={{ fontSize: '0.82rem', fontFamily: 'monospace' }}>{s.vlan_id || '—'}</td>
                                <td style={{ fontSize: '0.82rem' }}>{s.hidden ? '🔴 Sí' : 'No'}</td>
                                <td>
                                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: isActive ? '#16a34a' : '#dc2626' }}>
                                    ● {isActive ? 'Activo' : 'Desactivado'}
                                  </span>
                                </td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  <button type="button" className="dv-btn dv-btn-secondary" style={{ padding: '2px 8px', fontSize: '0.75rem', marginRight: 4 }}
                                    onClick={() => setEditSsid({ ...s, vlan_id: s.vlan_id ?? '' })}>
                                    ✏️
                                  </button>
                                  <button type="button" className="dv-btn dv-btn-danger" style={{ padding: '2px 8px', fontSize: '0.75rem' }}
                                    onClick={() => handleSsidDelete(s.id)}>
                                    🗑️
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {ssids.length === 0 && !editSsid && (
                    <div style={{ textAlign: 'center', padding: '1.5rem 0', color: 'var(--text-muted)' }}>
                      <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📡</div>
                      <p>Ninguna red WiFi (SSID) definida en este controlador.</p>
                    </div>
                  )}

                  {/* Botón nuevo SSID */}
                  {!editSsid && (
                    <button type="button" className="dv-btn dv-btn-primary" style={{ marginBottom: '1rem' }}
                      onClick={() => setEditSsid({ ...EMPTY_SSID })}>
                      + Añadir SSID
                    </button>
                  )}

                  {/* Formulario inline de SSID */}
                  {editSsid && (
                    <SsidInlineForm
                      ssid={editSsid}
                      saving={ssidSaving}
                      error={ssidError}
                      radiusServers={radiusServers}
                      onSave={handleSsidSave}
                      onCancel={() => { setEditSsid(null); setSsidError(''); }}
                    />
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Tab: APs ── */}
          {tab === 'aps' && (
            <div className={paneClass}>
              {!isEditing ? (
                <p style={{ color: '#94a3b8', padding: '1rem 0' }}>
                  Guarda el controlador primero para ver los APs vinculados.
                </p>
              ) : linkedAPs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem 0', color: '#94a3b8' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📶</div>
                  <p>Ningún AP asignado a este controlador todavía.</p>
                  <p style={{ fontSize: '0.85rem' }}>Asigna APs desde la pantalla de Puntos de Acceso.</p>
                </div>
              ) : (
                <>
                  {/* Diagrama desde el punto de vista de la controladora: agrupa las
                      cadenas de todos sus APs (una cadena por puerto). */}
                  <ConnectionDiagram
                    kind="controller"
                    id={controller?.id}
                    companyId={companyId}
                    title={`Conexiones de los APs de ${form.name || controller?.name || 'la controladora'}`}
                  />
                <div className="wol-table-wrap" style={{ marginTop: '0.5rem' }}>
                  <table className="wol-table">
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        <th>Vendor / Modelo</th>
                        <th>Estándar</th>
                        <th>MAC</th>
                        <th>Ubicación</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linkedAPs.map(ap => (
                        <tr key={ap.id}>
                          <td><strong>{ap.name}</strong></td>
                          <td>{ap.vendor_label || ap.vendor}{ap.model && <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}> · {ap.model}</span>}</td>
                          <td style={{ fontSize: '0.82rem' }}>{ap.wifi_standard_label || ap.wifi_standard}</td>
                          <td><code style={{ fontSize: '0.8rem' }}>{ap.mac_address || '—'}</code></td>
                          <td style={{ fontSize: '0.82rem' }}>{[ap.location_floor, ap.location_room].filter(Boolean).join(' · ') || ap.location_name || '—'}</td>
                          <td>
                            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: ap.status === 'active' ? '#15803d' : '#854d0e' }}>
                              ● {ap.status_label || ap.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
              )}
            </div>
          )}

          </fieldset>
          <FormDirtyDock
            show={isCards && isDirty}
            saving={saving}
            onDiscard={handleDiscard}
            onSave={handleSave}
          />
          </FormCardsShell>

        </form>
        </fieldset>
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="wifi_controller"
          ciId={controller?.id}
          tipoLegible="el controlador WiFi"
        />
      </div>
    </div>
  );
}

// ── SsidInlineForm ────────────────────────────────────────────────────────────
// Formulario inline para crear/editar un SSID dentro del WifiControllerForm.

function SsidInlineForm({ ssid, saving, error, onSave, onCancel, radiusServers = [] }) {
  const [f, setF] = useState({ ...ssid });
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    set(name, type === 'checkbox' ? checked : value);
  };

  const isEnterprise = f.security_mode === 'wpa2_enterprise' || f.security_mode === 'wpa3_enterprise';
  const selectedRadius = radiusServers.find(r => String(r.id) === String(f.radius_server_id));

  return (
    <div style={{ background: 'rgba(var(--accent-rgb), .07)', border: '1px solid rgba(var(--accent-rgb), .35)', borderRadius: 8, padding: '1rem 1.25rem', marginTop: '0.5rem' }}>
      <div style={{ fontWeight: 600, color: 'var(--accent)', marginBottom: '0.75rem', fontSize: '0.88rem' }}>
        {f.id ? `✏️ Editar SSID: "${f.name}"` : '+ Nuevo SSID'}
      </div>
      {error && <div style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: '0.5rem' }}>⚠ {error}</div>}

      <div className="row g-3">
        <div className="col-md-4">
          <label className="form-label">Nombre (SSID) <span className="text-danger">*</span></label>
          <input className="dv-input" name="name" value={f.name} onChange={handleChange} placeholder="CorpNet, Guest-WiFi…" maxLength={32} />
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>{f.name.length}/32 caracteres</div>
        </div>
        <div className="col-md-4">
          <label className="form-label">Modo de seguridad</label>
          <select className="dv-select" name="security_mode" value={f.security_mode} onChange={handleChange}>
            {SECURITY_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label">VLAN</label>
          <input className="dv-input" name="vlan_id" type="number" min="1" max="4094" value={f.vlan_id} onChange={handleChange} placeholder="10" />
        </div>
        <div className="col-md-2">
          <label className="form-label">Banda</label>
          <select className="dv-select" name="band_filter" value={f.band_filter} onChange={handleChange}>
            {BAND_FILTER_OPTIONS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
        </div>

        {isEnterprise && (
          <div className="col-12">
            <label className="form-label">Servidor RADIUS</label>
            {radiusServers.length > 0 && (
              <select
                className="dv-select"
                style={{ marginBottom: 6 }}
                value={f.radius_server_id || ''}
                onChange={e => {
                  const id = e.target.value || null;
                  const rs = radiusServers.find(r => String(r.id) === String(id));
                  setF(prev => ({
                    ...prev,
                    radius_server_id: id ? Number(id) : null,
                    radius_server: rs ? rs.host : prev.radius_server,
                    radius_port: rs ? rs.auth_port : prev.radius_port,
                  }));
                }}
              >
                <option value="">— Manual (introducir host) —</option>
                {radiusServers.map(r => (
                  <option key={r.id} value={r.id}>{r.name} ({r.host}:{r.auth_port})</option>
                ))}
              </select>
            )}
            {selectedRadius ? (
              <div style={{ padding: '4px 10px', background: 'rgba(var(--accent-rgb), .14)', border: '1px solid rgba(var(--accent-rgb), .35)', borderRadius: 6, fontSize: '0.78rem', color: 'var(--accent)' }}>
                🔐 {selectedRadius.host} · Auth:{selectedRadius.auth_port} · Acct:{selectedRadius.acct_port}
              </div>
            ) : (
              <div className="row g-2">
                <div className="col-md-8">
                  <input className="dv-input" name="radius_server" value={f.radius_server} onChange={handleChange} placeholder="10.0.1.5" />
                </div>
                <div className="col-md-4">
                  <input className="dv-input" name="radius_port" type="number" value={f.radius_port} onChange={handleChange} placeholder="1812" />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="col-md-2">
          <label className="form-label">Estado</label>
          <select className="dv-select" name="status" value={f.status} onChange={handleChange}>
            <option value="active">Activo</option>
            <option value="disabled">Desactivado</option>
          </select>
        </div>

        <div className="col-12">
          <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 4 }}>
            <input type="checkbox" name="hidden" checked={f.hidden} onChange={handleChange} />
            Red oculta (no broadcast SSID)
          </label>
          <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" name="client_isolation" checked={f.client_isolation} onChange={handleChange} />
            Aislamiento de clientes (no pueden comunicarse entre sí)
          </label>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: '1rem' }}>
        <button type="button" className="dv-btn dv-btn-primary" onClick={() => onSave(f)} disabled={saving}>
          {saving ? 'Guardando…' : (f.id ? 'Actualizar SSID' : 'Crear SSID')}
        </button>
        <button type="button" className="dv-btn dv-btn-secondary" onClick={onCancel} disabled={saving}>
          Cancelar
        </button>
      </div>
    </div>
  );
}
